import { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../store/authStore';

interface Material {
  key: string;
  filename: string;
  size: number;
  uploadedAt: string;
  chunkCount: number;
  textLength: number;
}

interface SearchResult {
  chunk: {
    id: string;
    content: string;
    metadata: Record<string, any>;
  };
  score: number;
}

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export function MaterialsPage() {
  const { user } = useAuthStore();
  const [materials, setMaterials] = useState<Material[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const authHeaders = {
    'X-Auth-User-Id': user?.id || '',
    'X-Auth-User-Email': user?.email || '',
  };

  const fetchMaterials = useCallback(async () => {
    if (!user) return;
    
    try {
      const res = await fetch(`${API_BASE}/api/rag/materials`, {
        headers: authHeaders,
      });
      
      if (res.ok) {
        const data = await res.json();
        setMaterials(data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch materials:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchMaterials();
  }, [fetchMaterials]);

  const handleUpload = async (files: FileList) => {
    if (!user || files.length === 0) return;
    
    setUploading(true);
    setUploadProgress('准备上传...');
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setUploadProgress(`处理 ${file.name} (${i + 1}/${files.length})...`);
      
      const formData = new FormData();
      formData.append('file', file);
      
      try {
        const res = await fetch(`${API_BASE}/api/rag/upload`, {
          method: 'POST',
          headers: authHeaders,
          body: formData,
        });
        
        if (res.ok) {
          const data = await res.json();
          setUploadProgress(`✅ ${file.name} 上传成功 (${data.chunkCount} 个片段)`);
        } else {
          const error = await res.json();
          setUploadProgress(`❌ ${file.name} 上传失败: ${error.error || error.details}`);
        }
      } catch (error) {
        setUploadProgress(`❌ ${file.name} 上传失败: ${(error as Error).message}`);
      }
    }
    
    await fetchMaterials();
    setUploading(false);
    setTimeout(() => setUploadProgress(''), 3000);
  };

  const handleDelete = async (key: string) => {
    if (!confirm('确定要删除这个材料吗？')) return;
    
    try {
      const res = await fetch(`${API_BASE}/api/rag/materials/${encodeURIComponent(key)}`, {
        method: 'DELETE',
        headers: authHeaders,
      });
      
      if (res.ok) {
        setMaterials(materials.filter(m => m.key !== key));
      }
    } catch (error) {
      console.error('Failed to delete material:', error);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim() || !user) return;
    
    setSearching(true);
    setSearchResults([]);
    
    try {
      const res = await fetch(`${API_BASE}/api/rag/search`, {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ query: searchQuery, topK: 5 }),
      });
      
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data.data || []);
      } else {
        const error = await res.json();
        alert(error.error || '搜索失败');
      }
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearching(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) {
      handleUpload(e.dataTransfer.files);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (!user) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <p className="text-slate-600">请先登录</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <h1 className="text-2xl font-bold text-slate-800 mb-6">📚 教学材料管理</h1>
      
      {/* Quick Actions */}
      <div className="flex gap-3 mb-6">
        <a
          href="/import"
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm font-medium"
        >
          <span>📤</span> 导入新资料
        </a>
      </div>
      
      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-8 mb-6 text-center transition-colors ${
          dragOver ? 'border-blue-500 bg-blue-50' : 'border-slate-300 hover:border-slate-400'
        }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <input
          type="file"
          id="file-upload"
          multiple
          accept=".pdf,.txt,.md,.markdown,.csv"
          className="hidden"
          onChange={(e) => e.target.files && handleUpload(e.target.files)}
          disabled={uploading}
        />
        <label htmlFor="file-upload" className="cursor-pointer">
          <div className="text-4xl mb-3">📄</div>
          <p className="text-slate-600 mb-2">
            {uploading ? uploadProgress : '拖拽文件到此处，或点击选择文件'}
          </p>
          <p className="text-sm text-slate-400">支持 PDF、TXT、Markdown、CSV 格式</p>
        </label>
      </div>

      {/* Search Area */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <h2 className="text-lg font-semibold text-slate-700 mb-3">🔍 搜索材料</h2>
        <div className="flex gap-3">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="输入搜索内容..."
            className="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={handleSearch}
            disabled={searching || !searchQuery.trim()}
            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {searching ? '搜索中...' : '搜索'}
          </button>
        </div>
        
        {/* Search Results */}
        {searchResults.length > 0 && (
          <div className="mt-4 space-y-3">
            {searchResults.map((result, i) => (
              <div key={i} className="p-3 bg-slate-50 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs text-slate-500">{result.chunk.id}</span>
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                    相关度: {(result.score * 100).toFixed(1)}%
                  </span>
                </div>
                <p className="text-sm text-slate-700">{result.chunk.content}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Materials List */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <h2 className="text-lg font-semibold text-slate-700 p-4 border-b">📁 已上传材料</h2>
        
        {loading ? (
          <div className="p-8 text-center text-slate-500">加载中...</div>
        ) : materials.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            <div className="text-4xl mb-2">📭</div>
            <p>暂无上传材料</p>
            <p className="text-sm">上传 PDF 或文本文档开始使用</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">文件名</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">大小</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">片段数</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">上传时间</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {materials.map((material) => (
                <tr key={material.key} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span>
                        {material.filename.endsWith('.pdf') ? '📕' : 
                         material.filename.endsWith('.md') ? '📝' : '📄'}
                      </span>
                      <span className="font-medium text-slate-800">{material.filename}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">{formatSize(material.size)}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{material.chunkCount}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">
                    {new Date(material.uploadedAt).toLocaleString('zh-CN')}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleDelete(material.key)}
                      className="text-red-500 hover:text-red-700 text-sm"
                    >
                      删除
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
